/**
 * Aspect Admin — Billing / subscription / invoices / usage caps.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, statusBadge, meter, toast, openModal, closeOverlay } from "./shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { INVOICES, USAGE_CAPS } from "../mock_data.js";

function upgradeModal(root) {
  const panel = openModal(root, {
    title: t("Change plan"),
    bodyHtml: `
      <div class="ap-stack" style="gap:0.6rem;">
        ${[
          { name: "Growth", price: "€490", desc: t("1,500 AI generations · 75 tours · 512 GB"), current: false },
          { name: "Scale", price: "€1,240", desc: t("3,000 AI generations · 150 tours · 1 TB"), current: true },
          { name: "Enterprise", price: t("Custom"), desc: t("Unlimited usage · SSO · custom roles · SLA"), custom: true, current: false },
        ]
          .map(
            (p) => `
          <div class="ap-panel ap-panel--pad" style="display:flex;align-items:center;gap:1rem;${p.current ? "border-color:var(--ap-cyan);box-shadow:0 0 0 3px rgba(0,178,216,0.12);" : ""}">
            <div style="flex:1;">
              <strong style="font-size:0.9rem;color:var(--ap-ink);">${p.name}</strong>
              ${p.current ? `<span class="ap-badge ap-badge--cyan" style="margin-left:0.5rem;">${t("Current")}</span>` : ""}
              <p class="ap-note" style="margin:0.2rem 0 0;">${p.desc}</p>
            </div>
            <div style="text-align:right;">
              <strong style="font-size:1.05rem;color:var(--ap-ink);">${p.price}</strong>
              <span class="ap-note" style="display:block;">${p.custom ? t("annual") : t("/ month")}</span>
            </div>
          </div>`
          )
          .join("")}
      </div>
    `,
    footHtml: `
      <button type="button" class="ap-btn ap-btn--ghost" data-mclose>${t("Close")}</button>
      <button type="button" class="ap-btn ap-btn--primary" data-mtalk>${t("Talk to sales")}</button>
    `,
  });
  panel?.querySelector("[data-mclose]")?.addEventListener("click", () => closeOverlay(root));
  panel?.querySelector("[data-mtalk]")?.addEventListener("click", () => {
    closeOverlay(root);
    toast(root, t("We'll reach out within one business day"), "ok");
  });
}

export function renderBilling(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const content = `
    ${pageHead(t("Billing"), t("Subscription, usage caps, and invoice history."), `
      <button type="button" class="ap-btn" id="bl-portal">${icon("external", 15)} ${t("Customer portal")}</button>
    `)}

    <div class="ap-grid ap-grid--main">
      <div class="ap-stack">
        <div class="ap-plancard">
          <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;flex-wrap:wrap;">
            <div>
              <span class="ap-badge ap-badge--cyan" style="margin-bottom:0.6rem;">${icon("star", 11)} ${t("Scale plan")}</span>
              <div class="ap-plancard__price">€1,240 <span>${t("/ month · billed monthly")}</span></div>
              <p class="ap-note" style="margin:0.5rem 0 0;max-width:30rem;">${t("Includes 3,000 AI generations, 150 published tours, 1 TB asset storage, and 10 team seats. Renews <strong>Aug 1, 2026</strong>.")}</p>
            </div>
            <div class="ap-row">
              <button type="button" class="ap-btn" id="bl-change">${t("Change plan")}</button>
              <button type="button" class="ap-btn ap-btn--ghost" id="bl-cancel">${t("Cancel")}</button>
            </div>
          </div>
        </div>

        <div class="ap-panel">
          <div class="ap-panel__head">
            <h3 class="ap-panel__title">${t("Invoices")}</h3>
            <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" id="bl-all">${icon("download", 14)} ${t("Download all")}</button>
          </div>
          <div class="ap-tablewrap ap-scroll">
            <table class="ap-table">
              <thead><tr><th>${t("Invoice")}</th><th>${t("Date")}</th><th class="ap-cell-num">${t("Amount")}</th><th>${t("Status")}</th><th></th></tr></thead>
              <tbody>
                ${INVOICES.map(
                  (inv) => `
                  <tr>
                    <td class="ap-cell-main">${inv.id}</td>
                    <td>${t(inv.date)}</td>
                    <td class="ap-cell-num">${inv.amount}</td>
                    <td>${statusBadge(inv.status)}</td>
                    <td class="ap-cell-actions"><button type="button" class="ap-iconbtn ap-iconbtn--bare" data-inv="${inv.id}" title="${t("Download")}">${icon("download", 15)}</button></td>
                  </tr>`
                ).join("")}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="ap-stack">
        <div class="ap-panel ap-panel--pad">
          <div class="ap-panel__head">
            <h3 class="ap-panel__title">${t("Usage this cycle")}</h3>
            <span class="ap-note">${t("Resets Aug 1")}</span>
          </div>
          <div class="ap-stack" style="gap:0.95rem;">
            ${USAGE_CAPS.map((u) => meter(u.label, u.used, u.cap, u.unit)).join("")}
          </div>
        </div>

        <div class="ap-insight">
          <div class="ap-insight__head">${icon("sparkles", 15)}<span class="ap-insight__tag">${t("Forecast")}</span></div>
          <h4 class="ap-insight__title">${t("You're on track to stay within plan")}</h4>
          <p class="ap-insight__body">${t("Projected end-of-cycle usage: 2,430 generations (81%). If the Meridian batch cadence doubles, consider the annual Scale plan — it would save <strong>€2,976 / year</strong>.")}</p>
          <div class="ap-insight__foot">
            <span class="ap-conf">${t("Confidence")} <span class="ap-conf__bars"><i class="on"></i><i class="on"></i><i class="on"></i></span></span>
            <button type="button" class="ap-btn ap-btn--soft ap-btn--sm" id="bl-annual">${t("See annual pricing")}</button>
          </div>
        </div>

        <div class="ap-panel ap-panel--pad">
          <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Payment method")}</h3></div>
          <div class="ap-row" style="justify-content:space-between;">
            <div class="ap-row">
              <span class="ap-kpi__icon">${icon("card", 16)}</span>
              <div>
                <strong style="font-size:0.845rem;color:var(--ap-ink);">SEPA Direct Debit</strong>
                <span class="ap-note" style="display:block;">EE38 •••• •••• 4501</span>
              </div>
            </div>
            <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" id="bl-pm">${t("Update")}</button>
          </div>
        </div>
      </div>
    </div>
  `;

  const el = renderShell(root, "/billing", content);
  if (!el) return;

  el.querySelector("#bl-change")?.addEventListener("click", () => upgradeModal(root));
  el.querySelector("#bl-annual")?.addEventListener("click", () => upgradeModal(root));
  el.querySelector("#bl-portal")?.addEventListener("click", () => toast(root, t("Opening secure customer portal…"), "info"));
  el.querySelector("#bl-cancel")?.addEventListener("click", () => toast(root, t("We'd hate to see you go — a specialist will reach out"), "info"));
  el.querySelector("#bl-all")?.addEventListener("click", () => toast(root, t("Invoice archive downloading…"), "ok"));
  el.querySelector("#bl-pm")?.addEventListener("click", () => toast(root, t("Payment method update requires re-authentication"), "info"));
  el.querySelectorAll("[data-inv]").forEach((b) => b.addEventListener("click", () => toast(root, t("{id}.pdf downloading…", { id: b.dataset.inv }), "ok")));
}
